/**
 * FIELD_OPTIONS canonical list and suggestColumnMapping pure function.
 * Moved from apps/mockups to @ooh/contracts (column-mapping-import change).
 */
import type { FieldOptionItem, ColumnMappingItem } from './import-job.js';

/** Canonical list of field options for the column mapping UI.
 * Aligned to Marta's 47-column NOSNIKI_HEADERS (szablon_OOH.xlsx).
 * One entry per NOSNIKI_HEADERS column — length must stay 47.
 */
export const FIELD_OPTIONS: FieldOptionItem[] = [
  // ── 1. Location ───────────────────────────────────────────────
  {
    key: 'region',
    label: 'Województwo',
    description: 'Województwo, w którym znajduje się nośnik.',
    group: 'general',
  },
  {
    key: 'district',
    label: 'Powiat',
    description: 'Powiat, w którym znajduje się nośnik.',
    group: 'general',
  },
  {
    key: 'agglomeration',
    label: 'Aglomeracja',
    description: 'Aglomeracja, w której znajduje się nośnik.',
    group: 'general',
  },
  {
    key: 'city',
    label: 'Miejscowość *',
    description: 'Miejscowość, w której znajduje się nośnik.',
    group: 'general',
    required: true,
  },
  {
    key: 'address',
    label: 'Adres *',
    description: 'Ulica i numer budynku.',
    group: 'general',
    required: true,
  },
  {
    key: 'road_number',
    label: 'Nr drogi (konstrukcja)',
    description: 'Numer drogi krajowej / wojewódzkiej przy konstrukcji.',
    group: 'general',
  },
  {
    key: 'latitude',
    label: 'Współrzędne - szerokość *',
    description: 'Współrzędna GPS: szerokość geograficzna.',
    group: 'general',
    required: true,
  },
  {
    key: 'longitude',
    label: 'Współrzędne - długość *',
    description: 'Współrzędna GPS: długość geograficzna.',
    group: 'general',
    required: true,
  },
  // ── 2. Identification ─────────────────────────────────────────
  {
    // Not required: the worker auto-allocates grw_id when the column is blank
    // (CU-86ca5euvn / PR #483). Marking it required wrongly blocked the mapping
    // step's "Dalej" button and the confirm-mapping endpoint (CU-86ca6pw1r).
    // Row-level identity is enforced by validateRow (grw_id OR supplier_number),
    // not by this UI-level required flag.
    key: 'grw',
    label: 'Numer GRW *',
    description: 'Unikalny identyfikator nośnika w systemie RW.',
    group: 'general',
  },
  {
    key: 'videocard_link',
    label: 'Link do karty video dla WF',
    description: 'URL do nagrania wideo dla wielkiego formatu.',
    group: 'general',
  },
  {
    key: 'identifier',
    label: 'Numer dostawcy',
    description: 'Unikalny identyfikator nośnika w systemie dostawcy.',
    group: 'general',
  },
  {
    key: 'supplier',
    label: 'Dostawca *',
    description: 'Firma będąca właścicielem lub operatorem nośnika.',
    group: 'general',
    required: true,
  },
  {
    key: 'type',
    label: 'Typ nośnika *',
    description: 'Rodzaj nośnika, np. billboard, citylight, słup ogłoszeniowy.',
    group: 'general',
    required: true,
  },
  {
    key: 'format',
    label: 'Format *',
    description: 'Wymiary nośnika ze słownika formatów, np. 12x3.',
    group: 'general',
    required: true,
  },
  // ── 3. Physical params ────────────────────────────────────────
  {
    key: 'width_m',
    label: 'Szerokość (m)',
    description: 'Szerokość powierzchni reklamowej w metrach.',
    group: 'physical',
  },
  {
    key: 'height_m',
    label: 'Wysokość (m)',
    description: 'Wysokość powierzchni reklamowej w metrach.',
    group: 'physical',
  },
  {
    key: 'dimension_notes',
    label: 'Wymiary niestandardowe',
    description:
      'Opis niestandardowych wymiarów dla produkcji, np. "trapez 5/3m x 4m". Wypełnij gdy nośnik nie jest prostokątem.',
    group: 'physical',
  },
  {
    key: 'material',
    label: 'Materiał',
    description: 'Materiał powierzchni nośnika, np. papier, folia, siatka.',
    group: 'physical',
  },
  {
    key: 'lighting',
    label: 'Oświetlenie',
    description: 'Czy nośnik jest podświetlany (T/N lub true/false).',
    group: 'physical',
  },
  // ── 4. Audience ───────────────────────────────────────────────
  {
    key: 'audience_estimate',
    label: 'Szacowana liczba odbiorcow',
    description: 'Szacowana liczba odbiorców nośnika.',
    group: 'general',
  },
  // ── 5. Multiformat ────────────────────────────────────────────
  {
    key: 'multiformat',
    label: 'Multiformat',
    description: 'Konfiguracja wielkoformatowa nośnika (np. 3/3, 2/3).',
    group: 'physical',
  },
  // ── 6. Common metadata ────────────────────────────────────────
  {
    key: 'wf_contact_person',
    label: 'Osoba kontakt WF/mural',
    description: 'Imię i nazwisko osoby kontaktowej przy WF i muralach.',
    group: 'general',
  },
  {
    key: 'location_description',
    label: 'Opis lokalizacji',
    description: 'Opisowa lokalizacja nośnika.',
    group: 'general',
  },
  {
    key: 'notes',
    label: 'Uwagi',
    description: 'Dodatkowe informacje o nośniku.',
    group: 'general',
  },
  // ── 7. Mural variant flags ────────────────────────────────────
  {
    key: 'mural_3d',
    label: '[Mural] Element 3D',
    description: 'Czy mural posiada element 3D (tak/nie).',
    group: 'mural_flags',
  },
  {
    key: 'mural_led_tape',
    label: '[Mural] Taśma LED',
    description: 'Czy mural posiada taśmę LED (tak/nie).',
    group: 'mural_flags',
  },
  {
    key: 'mural_neon_led',
    label: '[Mural] Neon LED',
    description: 'Czy mural posiada neon LED (tak/nie).',
    group: 'mural_flags',
  },
  {
    key: 'mural_fluo_uv',
    label: '[Mural] Fluo UV',
    description: 'Czy mural posiada farby fluo/UV (tak/nie).',
    group: 'mural_flags',
  },
  // ── 8. Costs / client prices ──────────────────────────────────
  {
    key: 'cost_rent',
    label: 'Czynsz',
    description: 'Miesięczny czynsz za powierzchnię nośnika (koszt wewnętrzny).',
    group: 'internal_cost',
  },
  {
    key: 'cost_exposure',
    label: 'Koszt ekspozycji',
    description: 'Koszt ekspozycji reklamy na nośniku (wewnętrzny).',
    group: 'internal_cost',
  },
  {
    key: 'price_catalog',
    label: 'Cena katalogowa',
    description: 'Cena katalogowa dla klienta.',
    group: 'client_price',
  },
  {
    key: 'price_discounted',
    label: 'Cena po rabacie',
    description: 'Cena dla klienta po uwzględnieniu rabatu.',
    group: 'client_price',
  },
  {
    key: 'price_last_minute',
    label: 'Cena last minute',
    description: 'Cena last minute dla klienta.',
    group: 'client_price',
  },
  {
    key: 'price_starter_rw',
    label: 'RW na start',
    description: 'Cena startowa RW dla klienta.',
    group: 'client_price',
  },
  {
    key: 'cost_production',
    label: 'Koszt produkcji',
    description: 'Koszt produkcji materiałów reklamowych (wewnętrzny).',
    group: 'internal_cost',
  },
  {
    key: 'client_production',
    label: 'Produkcja',
    description: 'Cena produkcji materiałów dla klienta.',
    group: 'client_price',
  },
  {
    key: 'cost_installation',
    label: 'Koszt montażu',
    description: 'Koszt montażu materiałów na nośniku (wewnętrzny).',
    group: 'internal_cost',
  },
  {
    key: 'client_installation',
    label: 'Montaż',
    description: 'Cena montażu dla klienta.',
    group: 'client_price',
  },
  {
    key: 'cost_dismantling',
    label: 'Koszt demontażu',
    description: 'Koszt demontażu materiałów z nośnika (wewnętrzny).',
    group: 'internal_cost',
  },
  {
    key: 'client_dismantling',
    label: 'Demontaż',
    description: 'Cena demontażu dla klienta.',
    group: 'client_price',
  },
  {
    key: 'cost_additional',
    label: 'Koszty dodatkowe',
    description: 'Pozostałe koszty wewnętrzne związane z nośnikiem.',
    group: 'internal_cost',
  },
  {
    key: 'client_additional_fees',
    label: 'Opłaty dodatkowe',
    description: 'Dodatkowe opłaty dla klienta.',
    group: 'client_price',
  },
  {
    key: 'mural_eco_production',
    label: '[Mural] Produkcja eco muralu',
    description: 'Cena produkcji eco muralu dla klienta.',
    group: 'client_price',
  },
  // ── 9. Tags ───────────────────────────────────────────────────
  {
    key: 'tag_1',
    label: 'Tag 1',
    description: 'Etykieta klasyfikacyjna 1.',
    group: 'general',
  },
  {
    key: 'tag_2',
    label: 'Tag 2',
    description: 'Etykieta klasyfikacyjna 2.',
    group: 'general',
  },
  {
    key: 'tag_3',
    label: 'Tag 3',
    description: 'Etykieta klasyfikacyjna 3.',
    group: 'general',
  },
  {
    key: 'tag_4',
    label: 'Tag 4',
    description: 'Etykieta klasyfikacyjna 4.',
    group: 'general',
  },
  {
    key: 'tag_5',
    label: 'Tag 5',
    description: 'Etykieta klasyfikacyjna 5.',
    group: 'general',
  },
  // ── 10. Photos ──────────────────────────────────────────────────
  {
    key: 'photo_urls',
    label: 'Zdjęcia',
    description: 'Linki do zdjęć, oddzielone przecinkami.',
    group: 'general',
  },
];

// ── FIELD_OPTION key → DB column translation (extracted to keep line count low) ──
export {
  FIELD_OPTION_KEY_TO_DB_COLUMN,
  TAG_DB_COLUMN,
  resolveMappedToColumn,
} from './import-job-key-to-column.js';

// ── suggestColumnMapping ──────────────────────────────────────────────────────

/**
 * Normalize a string for fuzzy matching:
 * - lowercase
 * - strip diacritics (Unicode normalization)
 * - collapse whitespace and punctuation to spaces
 * - trim
 */
function normalizeForMatch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Levenshtein distance between two strings.
 */
function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i]![j] = dp[i - 1]![j - 1]!;
      } else {
        dp[i]![j] = 1 + Math.min(dp[i - 1]![j]!, dp[i]![j - 1]!, dp[i - 1]![j - 1]!);
      }
    }
  }
  return dp[m]![n]!;
}

/**
 * Similarity score in [0, 1]: 1 = identical, 0 = completely different.
 */
function similarity(a: string, b: string): number {
  if (a.length === 0 && b.length === 0) return 1;
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshtein(a, b) / maxLen;
}

const SUGGEST_THRESHOLD = 0.7;

/**
 * Pure deterministic function that suggests a column mapping for each file header.
 * - Exact normalized label match wins.
 * - Otherwise picks the field option with the highest normalized Levenshtein similarity
 *   above SUGGEST_THRESHOLD.
 * - Returns mappedTo = null when no match is above threshold.
 */
export function suggestColumnMapping(
  fileHeaders: string[],
  fieldOptions: FieldOptionItem[],
): ColumnMappingItem[] {
  return fileHeaders.map((header, index) => {
    const normalizedHeader = normalizeForMatch(header);

    let bestKey: string | null = null;
    let bestScore = -1;

    for (const option of fieldOptions) {
      const normalizedLabel = normalizeForMatch(option.label);

      // Exact match shortcut
      if (normalizedLabel === normalizedHeader) {
        bestKey = option.key;
        bestScore = 1;
        break;
      }

      const score = similarity(normalizedHeader, normalizedLabel);
      if (score > bestScore) {
        bestScore = score;
        bestKey = score >= SUGGEST_THRESHOLD ? option.key : null;
      }
    }

    return {
      // Index-based, NOT header text: two source columns can share the same (possibly blank
      // or duplicated) header text, e.g. after a two-tier-header misdetection produces
      // several empty-string headers. An id derived from header text would collide for those
      // columns, causing every UI update keyed by `id` (mapping-phase.tsx `key={mapping.id}`,
      // applyMappingChange's `m.id === id` lookup) to silently apply to all of them at once
      // instead of just the edited row.
      id: `col-${String(index)}`,
      sourceColumn: header,
      mappedTo: bestScore >= SUGGEST_THRESHOLD ? bestKey : null,
    };
  });
}
